/**
 * /pay/:familyKey — Public, direct-payment page for enrolled families.
 *
 * Zero friction: no email gate, no orientation walkthrough, no login. Just the
 * family's tuition options with Stripe Payment Link buttons. Founder shares
 * one URL per family and they land on their exact payment card.
 *
 * Example: /pay/singletary  →  Myles ($325 one-time) + Gracen (4 tiered plans)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, ExternalLink, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const FamilyPay = () => {
  const { familyKey } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const r = await api.get(`/academy/pay/${familyKey}`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not load payment options.');
      } finally { setLoading(false); }
    })();
  }, [familyKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-6 text-white">
        <div className="max-w-md text-center" data-testid="pay-error">
          <div className="text-[10px] font-black tracking-[0.3em] uppercase text-red-400">Not Found</div>
          <h1 className="text-2xl font-black mt-3">Payment page unavailable</h1>
          <p className="text-sm text-zinc-400 mt-3">{err}</p>
          <Link to="/academy" className="inline-block mt-6 border-2 border-zinc-700 text-white px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em]" data-testid="pay-back">Return to Academy</Link>
        </div>
      </div>
    );
  }

  const { family_tuition } = data;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-14 pb-24 overflow-x-hidden" data-testid="family-pay">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="border-b-2 border-zinc-900 pb-6 mb-8">
          <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500">SAA · Direct Tuition Payment</div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2" data-testid="family-label">
            {family_tuition.family_label}
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
            Choose the plan that works for each of your student-athletes below. Every button opens a secure Stripe checkout — nothing else. Save this page and come back anytime.
          </p>
        </div>

        {/* Students · plans */}
        <div className="space-y-6" data-testid="students-list">
          {family_tuition.students.map((stu) => (
            <StudentBlock key={stu.student_key} student={stu} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-10 border-2 border-zinc-900 bg-zinc-950 p-5 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-[12px] text-zinc-400 leading-relaxed">
            Payments processed by <span className="text-white font-black">Stripe</span>. Questions? Email <a href="mailto:margaret@prsvr.com" className="text-orange-400 hover:text-orange-300 underline">margaret@prsvr.com</a> — she'll answer within 24 hours.
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentBlock = ({ student }) => {
  const isScholarship = student.plan_type === 'scholarship';
  const isOneTime     = student.plan_type === 'one_time';

  if (isScholarship) {
    const meta = Object.values(student.plans)[0] || {};
    return (
      <div className="border-2 border-emerald-500 bg-emerald-500/5 p-6" data-testid={`student-${student.student_key}`}>
        <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400">Full-Ride Scholarship</div>
        <h2 className="text-2xl font-black uppercase tracking-tight text-white mt-1">{student.student_name}</h2>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Standard Tuition</div>
            <div className="text-3xl font-black text-white line-through decoration-emerald-400/60">$8,000</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest font-black text-emerald-400">You Owe</div>
            <div className="text-4xl font-black text-emerald-400">$0.00</div>
          </div>
        </div>
        <div className="text-[12px] text-zinc-300 mt-5 leading-relaxed">{meta.schedule}</div>
      </div>
    );
  }

  const plans = Object.entries(student.plans);

  return (
    <div className="border-2 border-zinc-800 bg-zinc-950 p-6" data-testid={`student-${student.student_key}`}>
      <div className="text-[10px] uppercase tracking-widest font-black text-orange-500">
        {isOneTime ? 'Final Payment' : 'Choose One Plan'}
      </div>
      <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mt-1">{student.student_name}</h2>
      <div className={`mt-6 grid gap-3 ${plans.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
        {plans.map(([planKey, plan]) => (
          <PlanCard key={planKey} student_key={student.student_key} plan_key={planKey} plan={plan} isOneTime={isOneTime} />
        ))}
      </div>
    </div>
  );
};

const PlanTags = { monthly: '', quarterly: 'Discounted', biannual: 'Best 2-Payment', annual: 'Max Savings' };

const PlanCard = ({ student_key, plan_key, plan, isOneTime }) => {
  const rate = plan.rate;
  const paymentLink = plan.payment_link;
  const isDisabled = !paymentLink;
  const tag = PlanTags[plan_key] || (isOneTime ? 'One-Time' : '');

  return (
    <div className={`border-2 p-4 flex flex-col ${isDisabled ? 'border-zinc-800 bg-black' : 'border-zinc-800 bg-black hover:border-orange-500 transition-all'}`}
      data-testid={`plan-${student_key}-${plan_key}`}>
      {tag && <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mb-1.5">{tag}</div>}
      <div className="text-sm font-black uppercase tracking-tight text-white mb-1">{plan.label}</div>
      <div className="flex items-baseline gap-1.5 mb-2">
        <div className="text-3xl font-black text-orange-500">${rate.toLocaleString()}</div>
        <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">
          {plan_key === 'monthly' ? '/mo' : plan_key === 'quarterly' ? '/qtr' : ''}
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed flex-1">{plan.schedule}</p>
      {isDisabled ? (
        <div className="mt-3 text-[10px] text-amber-400 font-black uppercase tracking-widest border-2 border-amber-500/40 py-2 text-center">Margaret Will Invoice</div>
      ) : (
        <a href={paymentLink} target="_blank" rel="noreferrer"
          onClick={() => toast.success('Opening secure Stripe checkout…')}
          className="mt-3 bg-orange-500 hover:bg-orange-400 text-black py-2.5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2"
          data-testid={`pay-${student_key}-${plan_key}`}>
          <ExternalLink className="w-3 h-3" /> Pay ${rate.toLocaleString()}
        </a>
      )}
    </div>
  );
};

export default FamilyPay;
